import React from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import { Controller } from "react-hook-form";
import CardEmpty from "Utils/Cards/CardEmpty";
import LoadMask from "Utils/LoadMask/LoadMask";
import { DecisionSwal } from "Utils/Utils/DecisionSwal";
import { CorrelativoInicialPallet } from "./components/CorrelativoInicialPallet";
import { DetalleList } from "./components/DetalleList";
import {
    TIPO_PACAS,
    TIPO_SOBRANTE,
    useInventarioInicialForm,
} from "./hooks/useInventarioInicialForm";
import "./styles/InventarioInicial.css";

export const InventarioInicialTab = ({ nombreUsuario }) => {
    const hook = useInventarioInicialForm();

    const {
        formControl,
        formRegister,
        formErrors,
        correlativo,
        inventario,
        editable,
        fechaMaxima,
        primerMovimiento,
        detallesPacas,
        detallesSobrante,
        ValidarFormulario,
        GuardarInventario,
        EliminarInventario,
        cargando,
    } = hook;

    const ConfirmarGuardado = async () => {
        const valido = await ValidarFormulario();
        if (!valido) return;

        let mensaje = "";

        if (!detallesPacas.length && !detallesSobrante.length) {
            mensaje +=
                "No registró ninguna existencia, así que la bodega quedará " +
                "en cero a esa fecha. ";
        }

        mensaje +=
            "El inventario inicial solo se podrá corregir mientras no exista " +
            "ningún cierre. ¿Deseas continuar?";

        DecisionSwal(mensaje, "Sí, guardar", "Cancelar", GuardarInventario);
    };

    const ConfirmarEliminacion = () => {
        let mensaje = "Se eliminará el inventario inicial y los pallets que ";
        mensaje += "generó. ¿Deseas continuar?";

        DecisionSwal(mensaje, "Sí, eliminar", "Cancelar", EliminarInventario);
    };

    // sin correlativo inicial configurado no se puede registrar nada, porque
    // los pallets del inventario inicial se numeran a partir de él
    if (!correlativo.configurado) {
        return (
            <LoadMask loading={correlativo.cargando} blur>
                <CardEmpty noShadow>
                    <CorrelativoInicialPallet hook={correlativo} />
                </CardEmpty>
            </LoadMask>
        );
    }

    return (
        <div className="inventario-bodega__tab-wrapper">
            <LoadMask loading={cargando || correlativo.cargando} blur>
                <CardEmpty noShadow>
                    <h6 className="text-center">
                        Correlativo inicial de pallets
                    </h6>
                    <CorrelativoInicialPallet hook={correlativo} />
                    <div className="border-top pt-4" />
                    {editable && (
                        <div className="inventario-inicial__ayuda">
                            <p className="mb-1">
                                Registre aquí la existencia que hay físicamente
                                en bodega y que{" "}
                                <b>nunca se registró en el sistema</b>.
                            </p>
                            <p className="mb-0">
                                La fecha inicial es el punto de partida: todo lo
                                que se registró en el sistema después de esa
                                fecha se cuenta automáticamente en los cierres,
                                así que no debe incluirlo aquí.
                            </p>
                            {primerMovimiento && (
                                <p className="mb-0 mt-1">
                                    Ya hay movimientos de bodega registrados
                                    desde el{" "}
                                    {primerMovimiento.format(
                                        "DD/MM/YYYY HH:mm",
                                    )}
                                    , por eso la fecha inicial no puede ser
                                    posterior al{" "}
                                    {fechaMaxima
                                        ? fechaMaxima.format("DD/MM/YYYY HH:mm")
                                        : ""}
                                    .
                                </p>
                            )}
                        </div>
                    )}

                    {!editable && (
                        <div className="inventario-inicial__bloqueado">
                            Ya existen cierres de inventario, por lo que el
                            inventario inicial no se puede modificar. Para
                            corregirlo, debe reabrir y cancelar esos cierres,
                            empezando por el último.
                        </div>
                    )}

                    <div className="formulario-fields-row align-items-start">
                        <div className="formulario-field min-w-150">
                            <label className="text-nowrap">
                                Fecha y hora inicial
                            </label>
                            <Controller
                                name="fecha_inicial"
                                control={formControl}
                                rules={{
                                    required: "Debe indicar la fecha inicial",
                                }}
                                render={({ field }) => {
                                    // la hora límite solo restringe el día al que
                                    // pertenece: en los días anteriores valen las
                                    // 24 horas. Mientras no haya fecha elegida el
                                    // datepicker aplica la hora al día del límite,
                                    // así que ese es el día a comparar
                                    const fechaLimite = fechaMaxima || moment();
                                    const diaEditado = field.value
                                        ? moment(field.value)
                                        : fechaLimite;
                                    const esDiaLimite = diaEditado.isSame(
                                        fechaLimite,
                                        "day",
                                    );

                                    return (
                                        <DatePicker
                                            // el datepicker no expone focus(),
                                            // que es lo que llama shouldFocus
                                            ref={(datePicker) =>
                                                field.ref(
                                                    datePicker
                                                        ? {
                                                              focus: datePicker.setFocus,
                                                          }
                                                        : null,
                                                )
                                            }
                                            selected={field.value}
                                            onChange={field.onChange}
                                            showTimeSelect
                                            timeFormat="HH:mm"
                                            showPopperArrow={false}
                                            dateFormat="DD/MM/YYYY HH:mm"
                                            placeholderText="Fecha y hora inicial"
                                            maxDate={fechaLimite}
                                            minTime={moment().startOf("day")}
                                            maxTime={
                                                esDiaLimite
                                                    ? moment(fechaLimite)
                                                    : moment().endOf("day")
                                            }
                                            disabled={!editable}
                                            popperPlacement="bottom-start"
                                            popperModifiers={{
                                                flip: {
                                                    enabled: false,
                                                },
                                            }}
                                        />
                                    );
                                }}
                            />
                            {formErrors.fecha_inicial && (
                                <small className="text-danger">
                                    {formErrors.fecha_inicial.message}
                                </small>
                            )}
                        </div>

                        <div className="formulario-field min-w-150">
                            <label className="text-nowrap">Responsable</label>
                            <input
                                type="text"
                                className="form-control disabled-input"
                                value={nombreUsuario}
                                disabled
                            />
                        </div>

                        <div
                            className="formulario-field"
                            style={{ minWidth: "100px", flex: 3.1 }}
                        >
                            <label className="text-nowrap">Observaciones</label>
                            <input
                                type="text"
                                className="form-control"
                                disabled={!editable}
                                {...formRegister("observaciones", {})}
                            />
                        </div>
                    </div>

                    <DetalleList hook={hook} tipo={TIPO_PACAS} />
                    <div className="border-bottom pt-4" />
                    <DetalleList hook={hook} tipo={TIPO_SOBRANTE} />

                    <div className="d-flex justify-content-center mt-5 mb-4 gap-1rem">
                        {editable && inventario && (
                            <button
                                className="btn btn-danger"
                                onClick={ConfirmarEliminacion}
                            >
                                Eliminar
                            </button>
                        )}
                        {editable && (
                            <button
                                className="btn btn-primary"
                                onClick={ConfirmarGuardado}
                            >
                                Guardar
                            </button>
                        )}
                    </div>
                </CardEmpty>
            </LoadMask>
        </div>
    );
};
